import { useContext, useEffect } from 'react';
import { TabsContext } from './TabsContext';
import { CustomerRequestContext } from '../CustomerRequestContext';
import { SettingsContext } from '../../../contexts';
import { AppSettingNames, RequestStatus, usePrevious, SapTableNames } from '../../../../helpers'
import { DbType } from '../../../../helpers/Common';

// This component serves as "common point" where to react
// when data from formik changes (useEffect + values)
// In this component you can create useEffect cascade effects
// to populate dependant widgets with values

export const DataContainer = ({ ...props }) => {
    const { appSettingsECC, appSettingsS4 } = useContext(SettingsContext);

    const {
        state,
        getTableValues,
        getRespAreas,
        getSalesOffices,
        getSalesOrgsAsync,
        getCustomerGroup8,
        getCustomerGroup10,
        getCustGroupsWithAccGroupS4,
        getCustGroupsWithAccGroupECC,
        getReconciliationAccounts,
        getAccountingClerks,
        getTaxCategories,
        setIsBankInfoRequired,
        setHasAccountGroupDIST,
        setNoSendToSap,
        removeBankInfoErrors
    } = useContext(CustomerRequestContext);

    const {
        values,
        setFieldValue
    } = useContext(TabsContext);

    const {
        request,
        salesGroups,
        respAreas,
        customerGroups7,
        customerGroups8,
        customerGroups9,
        customerGroups10,
        userSalesOrgs,
        userCompanies,
        userBusinessCategories,
        salesOffices,
        custGroupsWithAccGroupECC,
        custGroupsWithAccGroupS4
    } = state;

    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            values.customerRequestId === 0
        ) {
            const exchangeRateTypeSetting = appSettingsS4.find(x => x.appSettingKey === AppSettingNames.ExchangeRateType).value;
            setFieldValue("exchangeRateType", exchangeRateTypeSetting);
        }
    }, []);

    // --- Primary Address Country change handler ---------

    const prevPACountryId = usePrevious(values.primaryAddress.countryId) || request.primaryAddress.countryId;
    useEffect(() => {
        if (prevPACountryId !== values.primaryAddress.countryId) {
            const italyCountrySetting = values.dbType === DbType.S4 ?
                appSettingsS4.find(x => x.appSettingKey === AppSettingNames.ItalyCountryId) :
                appSettingsECC.find(x => x.appSettingKey === AppSettingNames.ItalyCountryId);

            const italyCountryId = italyCountrySetting ? parseInt(italyCountrySetting.value) : 0;

            if (values.primaryAddress.countryId === italyCountryId)
                setFieldValue("hasVatNumber", true);
            
            if (values.status === RequestStatus.AccountReceivableApproval){
                const countries = getTableValues(SapTableNames.Countries, values.dbType);
                const country = countries.find(x => x.tableValueId === values.primaryAddress.countryId);
                setFieldValue("isCountryCanada", country.externalKey === "CA");
            }
        }
    }, [values.primaryAddress.countryId]);

    // --- isPerfumesCategory and status handler ---------

    useEffect(() => {
        setFieldValue("isDraftJWA", !values.isPerfumesCategory && RequestStatus.Draft === values.status);

        setFieldValue("isLogisticsJWA", !values.isPerfumesCategory && RequestStatus.Logistic === values.status);

    }, [values.isPerfumesCategory, values.status])

    // --- Sales Group loading and change handling ----------------

    useEffect(() => {
        if (salesGroups && salesGroups.length === 1 && values.salesGroupId === "")
            setFieldValue("salesGroupId", salesGroups[0].tableValueId);
    }, [salesGroups]);

    const prevSalesGroupId = usePrevious(values.salesGroupId) || request.salesGroupId;
    useEffect(() => {
        if (prevSalesGroupId !== values.salesGroupId && values.salesGroupId !== "") {
            setFieldValue("salesOfficeId", "");
            setFieldValue("responsibilityAreaId", "");
            getRespAreas(values.salesGroupId, values.dbType);

            if (values.salesOrganizationId !== "")
                getSalesOffices(values.salesGroupId, values.salesOrganizationId, values.dbType);
        }
    }, [values.salesGroupId]);

    // Customer groups one value handling -------

    useEffect(() => {
        if (
            customerGroups7 &&
            customerGroups7.length === 1 &&
            values.customerGroup7Id === ""
        )
            setFieldValue("customerGroup7Id", customerGroups7[0].tableValueId);
    }, [customerGroups7]);

    useEffect(() => {
        if (
            customerGroups8 &&
            customerGroups8.length === 1 &&
            values.customerGroup8Id === ""
        )
            setFieldValue("customerGroup8Id", customerGroups8[0].tableValueId);
    }, [customerGroups8]);

    useEffect(() => {
        if (
            customerGroups9 &&
            customerGroups9.length === 1 &&
            values.customerGroup9Id === ""
        )
            setFieldValue("customerGroup9Id", customerGroups9[0].tableValueId);
    }, [customerGroups9]);

    useEffect(() => {
        if (
            customerGroups10 &&
            customerGroups10.length === 1 &&
            values.customerGroup10Id === ""
        )
            setFieldValue("customerGroup10Id", customerGroups10[0].tableValueId);
    }, [customerGroups10]);

    // Resp Areas loading handling -------

    useEffect(() => {
        if (respAreas && respAreas.length === 1 && values.responsibilityAreaId == "")
            setFieldValue("responsibilityAreaId", respAreas[0].tableValueId);
    }, [respAreas]);

    const prevResponsibilityAreaId = usePrevious(values.responsibilityAreaId) || request.responsibilityAreaId;
    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            prevResponsibilityAreaId !== values.responsibilityAreaId &&
            values.responsibilityAreaId !== ""
        ) {
            setFieldValue("customerGroup10Id", "");
            getCustomerGroup10(values.responsibilityAreaId);
        }
    }, [values.responsibilityAreaId]);

    // --- Business Category loading and change handling ----------------

    useEffect(() => {
        if (
            userBusinessCategories &&
            userBusinessCategories.length === 1 &&
            values.businessCategoryId === ""
        ) {
            setFieldValue("businessCategoryId", userBusinessCategories[0].tableValueId);

            if (values.salesOrganizationId !== "") {
                if (values.dbType === DbType.ECC)
                    getCustGroupsWithAccGroupECC(values.salesOrganizationId, userBusinessCategories[0].tableValueId);
                else
                    getCustGroupsWithAccGroupS4(values.salesOrganizationId, userBusinessCategories[0].tableValueId);
            }
        }
    }, [userBusinessCategories]);

    const prevBusinessCategoryId = usePrevious(values.businessCategoryId) || request.businessCategoryId;
    useEffect(() => {
        if (
            prevBusinessCategoryId !== values.businessCategoryId &&
            values.businessCategoryId
        ) {
            const pbc = values.dbType === DbType.S4 ?
                appSettingsS4.find(x => x.appSettingKey === "PerfumesBusinessCategoryId") :
                appSettingsECC.find(x => x.appSettingKey === "PerfumesBusinessCategoryId");

            const isPerfumes: boolean = pbc.value === values.businessCategoryId.toString();

            setFieldValue("isPerfumesCategory", isPerfumes);
            setFieldValue("customerGroupId", "");

            if (values.dbType === DbType.S4) {
                setFieldValue("customerGroup6Id", "");
                setFieldValue("distributionChannelId", "");
            }

            if (values.salesOrganizationId !== "") {
                if (values.dbType === DbType.ECC)
                    getCustGroupsWithAccGroupECC(values.salesOrganizationId, values.businessCategoryId);
                else
                    getCustGroupsWithAccGroupS4(values.salesOrganizationId, values.businessCategoryId);
            }
        }
    }, [values.businessCategoryId]);

    // --- Company loading and change handling ----------

    useEffect(() => {
        if (userCompanies && userCompanies.length === 1 && values.companyId === "")
            setFieldValue("companyId", userCompanies[0].tableValueId);
    }, [userCompanies]);

    const prevCompanyId = usePrevious(values.companyId) || request.companyId;
    useEffect(() => {
        if (prevCompanyId !== values.companyId && values.companyId !== "") {
            setFieldValue("salesOrganizationId", "");
            setFieldValue("salesOfficeId", "");
            setFieldValue("customerGroupId", "");

            if (values.dbType === DbType.S4) {
                setFieldValue("customerGroup6Id", "");
                setFieldValue("distributionChannelId", "");
            }

            getSalesOrgsAsync(values.companyId, values.dbType);
            getSalesOffices(-1, -1, values.dbType);

            if (values.status === RequestStatus.AccountReceivableApproval) {
                setFieldValue("accountReceivable.reconcilationAccountId", "");
                getReconciliationAccounts(values.companyId, false, values.dbType);
                setFieldValue("accountReceivable.accountingClerkId", "");
                getAccountingClerks(values.companyId, null, false, values.dbType);
            }
        }
    }, [values.companyId]);

    // --- Sales Orgs loading and change handling --------------

    useEffect(() => {
        if (
            userSalesOrgs &&
            userSalesOrgs.length === 1 &&
            values.salesOrganizationId === ""
        ) {
            setFieldValue("salesOrganizationId", userSalesOrgs[0].tableValueId);
            setFieldValue("accountReceivable.taxCategories", []);

            if (values.businessCategoryId !== "") {
                if (values.dbType === DbType.ECC)
                    getCustGroupsWithAccGroupECC(userSalesOrgs[0].tableValueId, values.businessCategoryId);
                else
                    getCustGroupsWithAccGroupS4(userSalesOrgs[0].tableValueId, values.businessCategoryId);
            }
        }
    }, [userSalesOrgs]);

    const prevSalesOrganizationId = usePrevious(values.salesOrganizationId) || request.salesOrganizationId;
    useEffect(() => {
        if (
            prevSalesOrganizationId !== values.salesOrganizationId &&
            values.salesOrganizationId !== ""
        ) {
            setFieldValue("salesOfficeId", "");
            setFieldValue("customerGroupId", "");

            if (values.dbType === DbType.S4) {
                setFieldValue("customerGroup8Id", "");
                getCustomerGroup8(values.salesOrganizationId);
            }

            if (values.salesGroupId !== "")
                getSalesOffices(values.salesGroupId, values.salesOrganizationId, values.dbType);

            if (values.businessCategoryId !== "") {
                if (values.dbType === DbType.ECC)
                    getCustGroupsWithAccGroupECC(values.salesOrganizationId, values.businessCategoryId);
                else
                    getCustGroupsWithAccGroupS4(values.salesOrganizationId, values.businessCategoryId);
            }

            if (values.status === RequestStatus.AccountReceivableApproval) {
                //getDeliveringPlants(values.salesOrganizationId, values.dbType);
                setFieldValue("accountReceivable.taxCategories", []);
                getTaxCategories(values.salesOrganizationId, false, values.dbType);
            }
        }
    }, [values.salesOrganizationId]);

    // --- Credit Management loading and change handling --------------

    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            values.status === RequestStatus.CreditRiskEvaluation &&
            values.riskEvaluation.creditLimitCurrencyId !== ""
        ) {
            const cmToUpdateCurrency = { ...values.creditManagement };

            cmToUpdateCurrency.creditTypes.forEach(x => {
                x.currencyId = values.riskEvaluation.creditLimitCurrencyId;
            });
            
            setFieldValue("creditManagement", cmToUpdateCurrency);
        }
    }, [values.riskEvaluation?.creditLimitCurrencyId]);

    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            values.creditManagement &&
            values.creditManagement.segmentId !== ""
        ) {
            const cm = { ...values.creditManagement };
            setFieldValue("creditManagement", cm);
        }
    }, [values.creditManagement?.segmentId]);

    // --- Sales Offices loading handling -------

    useEffect(() => {
        if (salesOffices && salesOffices.length === 1 && values.salesOfficeId == "")
            setFieldValue("salesOfficeId", salesOffices[0].tableValueId);
    }, [salesOffices]);

    // --- Customer Group ECC loading handling --------------

    useEffect(() => {
        if (
            custGroupsWithAccGroupECC &&
            custGroupsWithAccGroupECC.length === 1 &&
            values.customerGroupId === ""
        )
            setFieldValue("customerGroupId", custGroupsWithAccGroupECC[0].customerGroup.tableValueId);
    }, [custGroupsWithAccGroupECC]);

    // --- Customer Group S4 loading handling --------------

    useEffect(() => {
        if (custGroupsWithAccGroupS4) {
            if (values.customerGroupId === ""){
                const uniqueValues = custGroupsWithAccGroupS4
                    .map(x => x.customerGroup)
                    .filter((item1, index, self) =>
                        index === self.findIndex(item2 =>
                            item2.externalKey === item1.externalKey &&
                            item2.value === item1.value
                        )
                    );
                
                if (uniqueValues.length === 1)
                    setFieldValue("customerGroupId", custGroupsWithAccGroupS4[0].customerGroup.tableValueId);
            }

            if (values.customerGroup6Id === ""){
                const uniqueValues = custGroupsWithAccGroupS4
                    .map(x => x.customerGroup6)
                    .filter((item1, index, self) =>
                        index === self.findIndex(item2 =>
                            item2.externalKey === item1.externalKey &&
                            item2.value === item1.value
                        )
                    );
                
                if (uniqueValues.length === 1)
                    setFieldValue("customerGroup6Id", custGroupsWithAccGroupS4[0].customerGroup6.tableValueId);
            }

            if (values.distributionChannelId === ""){
                const uniqueValues = custGroupsWithAccGroupS4
                    .map(x => x.distributionChannel)
                    .filter((item1, index, self) =>
                        index === self.findIndex(item2 =>
                            item2.externalKey === item1.externalKey &&
                            item2.value === item1.value
                        )
                    );
                
                if (uniqueValues.length === 1)
                    setFieldValue("distributionChannelId", custGroupsWithAccGroupS4[0].distributionChannel.tableValueId);
            }
        }
    }, [custGroupsWithAccGroupS4]);

    // --- Customer Group 6 change handling -------------

    const prevCustomerGroupId = usePrevious(values.customerGroupId) || request.customerGroupId;
    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            prevCustomerGroupId !== values.customerGroupId &&
            values.customerGroupId !== ""
        ) {
            const filteredCg6 = custGroupsWithAccGroupS4.filter(x => x.customerGroup.tableValueId === values.customerGroupId);

            if (filteredCg6.length === 1)
                setFieldValue("customerGroup6Id", filteredCg6[0].customerGroup6.tableValueId);
            else
                setFieldValue("customerGroup6Id", "");
        }
    }, [values.customerGroupId]);

    // --- Distribution Channel change handling -------------

    const prevCustomerGroup6Id = usePrevious(values.customerGroup6Id) || request.customerGroup6Id;
    useEffect(() => {
        if (
            values.dbType === DbType.S4 &&
            prevCustomerGroup6Id !== values.customerGroup6Id &&
            values.customerGroup6Id !== ""
        ) {
            const filteredDistributionChannel = custGroupsWithAccGroupS4.filter(x => x.customerGroup6.tableValueId === values.customerGroup6Id);

            if (filteredDistributionChannel.length === 1)
                setFieldValue("distributionChannelId", filteredDistributionChannel[0].distributionChannel.tableValueId);
            else
                setFieldValue("distributionChannelId", "");
        }
    }, [values.customerGroup6Id]);

    // --- Accoung Group Mapping ECC change handling -------------

    useEffect(() => {
        if (
            custGroupsWithAccGroupECC &&
            custGroupsWithAccGroupECC.length > 0 &&
            values.customerGroupId !== "" &&
            values.customerGroupId !== 0 &&
            values.salesOrganizationId !== "" &&
            values.salesOrganizationId !== 0 &&
            values.businessCategoryId !== "" &&
            values.businessCategoryId !== 0
        ) {
            const targetCG = custGroupsWithAccGroupECC.find(x => x.customerGroup.tableValueId === values.customerGroupId);

            if (targetCG) {
                // Check if bank info is optional
                const accGroupNoBankInfoIds = appSettingsECC.find(x => x.appSettingKey === AppSettingNames.AccGroupNoBankInfoIds);

                if (
                    accGroupNoBankInfoIds &&
                    accGroupNoBankInfoIds.value &&
                    accGroupNoBankInfoIds.value !== ""
                ) {
                    const ids = accGroupNoBankInfoIds.value.split(',').map(x => parseInt(x));
                    const isBankInfoOptional = ids.includes(targetCG.accountGroup.tableValueId);

                    if (isBankInfoOptional)
                        removeBankInfoErrors();

                    setIsBankInfoRequired(!isBankInfoOptional);
                }

                // Check if resale margin jewellery is needed (using direct comparison of the external key, no settings though)
                setHasAccountGroupDIST(targetCG.accountGroup.externalKey === "DIST");

                // Check if this request can be not send to sap
                //setNoSendToSap(targetCG.accountGroup.externalKey === "FR");
            }
        }
    }, [custGroupsWithAccGroupECC, values.salesOrganizationId, values.businessCategoryId, values.customerGroupId]);

    // --- Accoung Group Mapping S4 change handling -------------

    useEffect(() => {
        if (
            custGroupsWithAccGroupS4 &&
            custGroupsWithAccGroupS4.length > 0 &&
            values.customerGroupId !== "" &&
            values.customerGroupId !== 0 &&
            values.salesOrganizationId !== "" &&
            values.salesOrganizationId !== 0 &&
            values.businessCategoryId !== "" &&
            values.businessCategoryId !== 0
        ) {
            const targetCG = custGroupsWithAccGroupS4.find(x => x.customerGroup.tableValueId === values.customerGroupId);

            if (targetCG) {
                // Check if bank info is optional
                const accGroupNoBankInfoIds = appSettingsS4.find(x => x.appSettingKey === AppSettingNames.AccGroupNoBankInfoIds);

                if (
                    accGroupNoBankInfoIds &&
                    accGroupNoBankInfoIds.value &&
                    accGroupNoBankInfoIds.value !== ""
                ) {
                    const ids = accGroupNoBankInfoIds.value.split(',').map(x => parseInt(x));
                    const isBankInfoOptional = ids.includes(targetCG.accountGroup.tableValueId);

                    if (isBankInfoOptional)
                        removeBankInfoErrors();

                    setIsBankInfoRequired(!isBankInfoOptional);
                }

                // Check if resale margin jewellery is needed (using direct comparison of the external key, no settings though)
                setHasAccountGroupDIST(targetCG.accountGroup.externalKey === "DIST");

                // Check if this request can be not send to sap
                //setNoSendToSap(targetCG.accountGroup.externalKey === "FR");
            }
        }
    }, [custGroupsWithAccGroupS4, values.salesOrganizationId, values.businessCategoryId, values.customerGroupId]);

    return (
        <div>
            {props.children}
        </div>
    )
}